import { Stack, Typography } from '@mui/material'
import {
  $ApronNone,
  $TopWithDiameter,
  $TopWithSide,
  $TopWithWidthAndLength,
} from 'element-domain/src/__generated/enhanced-model'
import { LeafTypes, SeatingTypes } from 'element-domain/src/__generated/model'
import { SEATING_MAP } from 'element-domain/src/data/table-data'
import { FINISH_MAP, SHEEN_MAP, WOOD_SPECIES_MAP } from 'element-domain/src/data/texture-data'
import { THICKNESS_OPTIONS, TOP_DISTRESS_LEVEL_OPTIONS, TOP_EDGE_STYLE_OPTIONS } from 'element-domain/src/data/top-data'

import { useResourceUrl } from '@salsita-ai/platform-project-sdk/react'
import { Summary } from '@salsita-ai/platform-project-sdk/ui'

import { configuratorInterface } from '../../systems/configurator-interface'
import CompactDimensions from './helper-components/CompactDimensions'
import SummarySubItem from './helper-components/SummarySubItem'

export default function SummaryPage() {
  const { getResourceUrl } = useResourceUrl()
  const $table = configuratorInterface.useRootEntityState()

  // Helper function to get dimensions based on top type
  const getTopDimensions = () => {
    const dimensions = []

    if ($TopWithWidthAndLength.is($table.$top)) {
      dimensions.push({
        label: 'Width',
        value: `${$table.$top.width.toFixed(2)}"`,
      })
      dimensions.push({
        label: 'Length',
        value: `${$table.$top.length.toFixed(2)}"`,
      })
    }

    if ($TopWithSide.is($table.$top)) {
      dimensions.push({
        label: 'Side',
        value: `${$table.$top.side.toFixed(2)}"`,
      })
    }

    if ($TopWithDiameter.is($table.$top)) {
      dimensions.push({
        label: 'Diameter',
        value: `${$table.$top.diameter.toFixed(2)}"`,
      })
    }

    // Add thickness to dimensions
    dimensions.push({
      label: 'Thickness',
      value: `${$table.$top.thickness.toFixed(2)}" (${THICKNESS_OPTIONS[$table.$top.thicknessType].label})`,
      price: $table.$thicknessPrice,
      showPrice: true,
    })

    return dimensions
  }

  return (
    <Stack sx={{ gap: 3 }}>
      {/* Header */}
      <Stack sx={{ gap: 1 }}>
        <Typography variant="h5">Order Summary</Typography>
        <Typography variant="body2" color="text.secondary">
          Review your custom table configuration
        </Typography>
      </Stack>

      {/* Main Content */}
      <Stack sx={{ gap: 3 }}>
        {/* Table Top Section */}
        <Stack>
          <Typography variant="h6">Table Top</Typography>
          <Stack component="ul" sx={{ m: 0, p: 0 }}>
            <Summary.Item
              title={`${$table.$top.$topDataMap[$table.$top.type].label}`}
              description="Shape"
              imageSrc={getResourceUrl($table.$top.$topDataMap[$table.$top.type].thumbnailUrl)}
              action={<Typography>${$table.$top.$price.toFixed(2)}</Typography>}
            />

            <CompactDimensions dimensions={getTopDimensions()} />

            <SummarySubItem
              title={`${TOP_EDGE_STYLE_OPTIONS[$table.$top.edgeStyle].label}`}
              subtitle="Edge Style"
              secondarySlot={<Typography>{refinePrice($table.$edgeStylePrice)}</Typography>}
            />
            <SummarySubItem
              title={`${TOP_DISTRESS_LEVEL_OPTIONS[$table.$top.distressLevel].label}`}
              subtitle="Distress Level"
              secondarySlot={<Typography>{refinePrice($table.$distressPrice)}</Typography>}
            />
            <SummarySubItem
              title={`${FINISH_MAP[$table.$top.finish].label}`}
              subtitle="Finish"
              secondarySlot={<Typography>{refinePrice($table.$topFinishPrice)}</Typography>}
            />
            {!$ApronNone.is($table.$top.$apron) && (
              <SummarySubItem
                title="With Apron"
                subtitle="Apron"
                secondarySlot={<Typography>{refinePrice($table.$apronPrice)}</Typography>}
              />
            )}
            {$table.$top.withGrooves && (
              <SummarySubItem
                title="With Grooves"
                subtitle="Grooves"
                secondarySlot={<Typography>{refinePrice($table.$groovesPrice)}</Typography>}
              />
            )}
            {$table.$top.withCornerRadius && (
              <SummarySubItem
                title="With Rounded Corners"
                subtitle="Corner Radius"
                secondarySlot={<Typography>{refinePrice($table.$cornerRadiusPrice)}</Typography>}
              />
            )}
            {$table.$top.leafType !== LeafTypes.NoLeaf && (
              <SummarySubItem
                title={`${$table.$top.leafAmount}x ${$table.$top.$leafDataMap[$table.$top.leafType].label} (${$table.$top.$leafDataMap[$table.$top.leafType].length}")`}
                subtitle="Leaf"
                secondarySlot={<Typography>{refinePrice($table.$leafPrice)}</Typography>}
              />
            )}
          </Stack>
        </Stack>

        {/* Base Section */}
        <Stack>
          <Typography variant="h6">Base</Typography>
          <Stack component="ul" sx={{ m: 0, p: 0 }}>
            <Summary.Item
              title={`${$table.$base.$baseDataMap[$table.$base.type].label}`}
              description={`${$table.$base.$baseDataMap[$table.$base.type].baseType}`}
              imageSrc={getResourceUrl($table.$base.$baseDataMap[$table.$base.type].thumbnailUrl)}
              action={<Typography>${$table.$base.$price.toFixed(2)}</Typography>}
            />
            {$table.$base.hasCrossbrace && (
              <SummarySubItem
                title="With Cross Brace"
                subtitle="Cross Brace"
                secondarySlot={<Typography>{refinePrice($table.$crossbracePrice)}</Typography>}
              />
            )}
            <SummarySubItem
              title={`${FINISH_MAP[$table.$base.finish].label}`}
              subtitle="Finish"
              secondarySlot={<Typography>{refinePrice($table.$baseFinishPrice)}</Typography>}
            />
          </Stack>
        </Stack>

        {/* Materials & Finishes Section */}
        <Stack>
          <Typography variant="h6">Materials & Finishes</Typography>
          <Stack component="ul" sx={{ m: 0, p: 0 }}>
            <SummarySubItem
              title={`${WOOD_SPECIES_MAP[$table.woodSpecies].label} (${formatPercentage($table.$woodSpeciesRate)})`}
              subtitle="Wood Species"
              secondarySlot={<Typography>{refinePrice($table.$woodSpeciesPrice)}</Typography>}
            />
            <SummarySubItem
              title={`${SHEEN_MAP[$table.sheen].label}`}
              subtitle="Sheen"
              secondarySlot={<Typography>{refinePrice($table.$sheenPrice)}</Typography>}
            />
          </Stack>
        </Stack>

        {/* Accessories Section */}
        {$table.seatingType !== SeatingTypes.NoSeating && (
          <Stack>
            <Typography variant="h6">Accessories</Typography>
            <Stack component="ul" sx={{ m: 0, p: 0 }}>
              <SummarySubItem
                title={`${$table.seatingType === SeatingTypes.Benches ? `${$table.seatingAmount}x` : ''} ${SEATING_MAP[$table.seatingType].label}`}
                subtitle="Seating"
                secondarySlot={<Typography>{refinePrice($table.$seatingPrice)}</Typography>}
              />
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}

const refinePrice = (price: number) => {
  const prefix = price >= 0 ? '+' : '-'
  if (price === 0) {
    return ''
  }
  return `${prefix}$${Math.abs(price).toFixed(2)}`
}

const formatPercentage = (rate: number) =>
  new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 2 }).format(rate)
